import { Tab, Tabs } from '@nextui-org/react'

import { Crisp } from 'crisp-sdk-web'
import {
    useEditorMode
} from 'website/src/lib/hooks'

import NextLink from 'next/link'
import { useRouter } from 'next/router'
import { Fragment, useEffect, useMemo, useState } from 'react'

import { DesktopComputerIcon } from '@heroicons/react/outline'
import { CodeIcon } from '@heroicons/react/solid'
import { useStore } from '@nanostores/react'
import {
    Button,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownTrigger,
    useDisclosure,
} from '@nextui-org/react'
import { ShareIcon } from 'lucide-react'
import path from 'path'
import { Banner } from 'website/src/components/editor/Banner'
import { CurrentUsers } from 'website/src/components/editor/CurrentUsers'
import { GithubSyncButton } from 'website/src/components/editor/GithubSynButton'
import { PRButton } from 'website/src/components/editor/PRButton'
import { EditorProjectSignupModal } from 'website/src/components/editor/SignupModal'
import { atoms } from 'website/src/lib/atoms'
import { contentXPadding, toolbarHeight } from 'website/src/lib/constants'
import { PagesTree, TableOfContentsEntry } from 'website/src/lib/editor-types'
import { pagesTreeBfs, withEditorBasePath } from 'website/src/lib/editor-utils'
import { env } from 'website/src/lib/env'
import { useRootProps } from 'website/src/lib/hooks'
import { LeftBar } from './LeftBar'

function useTableOfContents(tableOfContents: TableOfContentsEntry[]) {
    let [currentSection, setCurrentSection] = useState(tableOfContents[0]?.slug)

    useEffect(() => {
        if (tableOfContents.length === 0) return
        let headings = tableOfContents.map((entry) => {
            let el = document.getElementById(entry.slug)
            if (!el) return

            let style = window.getComputedStyle(el)
            let scrollMt = parseFloat(style.scrollMarginTop)

            let top = window.scrollY + el.getBoundingClientRect().top - scrollMt
            return { slug: entry.slug, top }
        })
        function onScroll() {
            let top = window.scrollY
            let current = headings[0]?.slug
            if (!current) {
                return
            }
            for (let heading of headings) {
                if (!heading) {
                    continue
                }
                if (top >= heading.top) {
                    current = heading.slug
                } else {
                    break
                }
            }
            setCurrentSection(current)
        }
        window.addEventListener('scroll', onScroll, { passive: true })
        onScroll()
        return () => {
            window.removeEventListener('scroll', onScroll, {
                passive: true,
            } as any)
        }
    }, [tableOfContents])

    return currentSection
}

function EmptySite() {
    return (
        <div className='flex flex-col items-center justify-center h-full'>
            No pages
        </div>
    )
}

function MobileNotice() {
    if (env.NEXT_PUBLIC_ENV === 'development') {
        return null
    }
    return (
        <div className='fixed flex md:hidden flex-col gap-6 p-6 items-center z-50 justify-center w-full h-full bg-gray-50 dark:bg-gray-800'>
            <div className='text-center font-bold'>
                The editor only works on Desktop, please open the website in
                your computer
            </div>
            <DesktopComputerIcon className='w-8' />
        </div>
    )
}

function DemoSignupButton({}) {
    const { userId } = useRootProps()

    if (userId) {
        return null
    }
    return (
        <Button
            onClick={() => {
                atoms.showSignupModal.set(true)
            }}
            // startContent={<ShareIcon className='w-4' />}
            className='truncate relative flex '
        >
            Sign Up
        </Button>
    )
}

export function EditorLayoutWithFiles({ children }) {
    let router = useRouter()

    const { isPR, pagesTree, orgs, siteId, sites, branchId } = useRootProps()

    const topWebsitePadding = useStore(atoms.topWebsitePadding)

    return (
        <div className='flex flex-col h-screen items-stretch'>
            <div className='mx-auto w-full grow flex justify-between'>
                <LeftBar />

                <div
                    style={{ height: '100vh' }}
                    className='min-w-0 relative max-h-screen grow'
                >
                    <div
                        style={{
                            height: `calc(100vh - ${0}px)`,
                        }}
                        id='editor-scrollable-content'
                        className='overflow-y-auto overflow-x-hidden'
                    >
                        {pagesTree?.length ? children : <EmptySite />}
                    </div>
                    <div
                        style={{
                            left: 0,
                            top: 0,
                            right: 0,
                            height: toolbarHeight + topWebsitePadding,
                            position: 'absolute',
                        }}
                    >
                        <Toolbar />
                    </div>
                </div>
                <Banner />
                <MobileNotice />
                <EditorProjectSignupModal />
            </div>
        </div>
    )
}

function Toolbar() {
    const topWebsitePadding = useStore(atoms.topWebsitePadding)
    const mode = useEditorMode()
    const previewGradient = (
        <div className='from-[--background] w-full via-[--background] to-transparent shrink-0 bg-gradient-to-b h-full'></div>
    )
    const markdownGradient = (
        <div className='from-[--markdownEditorBg] w-full via-[--markdownEditorBg] to-transparent bg-gradient-to-b h-full' />
    )

    let gradients = (() => {
        if (mode === 'markdown') {
            return markdownGradient
        }
        if (mode === 'wysiwyg') {
            return previewGradient
        }
        if (mode === 'split') {
            return (
                <>
                    {markdownGradient}
                    {previewGradient}
                </>
            )
        }
        return null
    })()

    return (
        <div className='w-full h-full flex justify-end'>
            <div
                style={{
                    paddingTop: topWebsitePadding,
                    paddingLeft: contentXPadding,
                    paddingRight: contentXPadding,
                }}
                className='flex relative w-full items-center justify-end grow-0 shrink-0 gap-6'
            >
                <div
                    style={{
                        top: 0,
                        left: 0,
                        right: 0,
                        height: topWebsitePadding + 80,

                        scrollbarGutter: 'stable',
                    }}
                    className='flex text-sm pointer-events-none font-mono overflow-y-auto w-full absolute justify-stretch items-stretch'
                >
                    {gradients}
                </div>
                {/* <BreadCrumbs /> */}
                <div className='grow'></div>
                <CurrentUsers />

                <GithubSyncButton />
                {/* <ExportButton /> */}
                <DemoSignupButton />
                <PRButton />
                <MarkdownSwitch />
            </div>
        </div>
    )
}

function MarkdownSwitch({}) {
    let router = useRouter()
    const mode = useEditorMode()
    return (
        <Tabs
            // size='sm'
            onSelectionChange={(mode) => {
                router.replace(
                    {
                        // pathname: router.asPath,

                        query: {
                            ...router.query,
                            mode,
                        },
                    },
                    undefined,
                    { shallow: true },
                )
            }}
            selectedKey={mode}
            className='z-10 tracking-tighter'
            classNames={{
                tabList: 'h-[42px]',
            }}
        >
            <Tab key='wysiwyg' title={<ParagraphIcon className='w-5' />}></Tab>
            <Tab
                key='markdown'
                title={<CodeIcon className='shrink-0 w-[22px]' />}
            ></Tab>
            <Tab key='split' title={<PanelsIcon className='w-5' />}></Tab>
        </Tabs>
    )
}

export function ParagraphIcon(props) {
    return (
        <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' {...props}>
            <path
                fill='currentColor'
                d='M9 20v-6q-2.075 0-3.538-1.463T4 9q0-2.075 1.463-3.538T9 4h9v2h-2v14h-2V6h-3v14H9Z'
            ></path>
        </svg>
    )
}

export function PanelsIcon(props) {
    return (
        <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' {...props}>
            <path
                fill='currentColor'
                fillRule='evenodd'
                d='M2 1L1 2v12l1 1h12l1-1V2l-1-1H2Zm0 13V2h7v12H2Z'
                clipRule='evenodd'
            ></path>
        </svg>
    )
}

function BreadCrumbs() {
    const { pagesTree, pageId } = useRootProps()
    const bread = useMemo(() => {
        const parents = new Map()
        const nodes = pagesTreeBfs(pagesTree, (x, parent) => {
            parents.set(x, parent)
            return x.pageId === pageId
        })
        const node = nodes[nodes.length - 1]
        let current = node
        let bread: PagesTree[] = []
        while (current) {
            bread.unshift(current)
            current = parents.get(current)
        }

        return bread.slice(0, -1).map((node) => {
            return node
        })
    }, [pagesTree])

    const router = useRouter()
    return (
        <div className='flex space-x-2 text-sm truncate'>
            {bread.map((x, i) => {
                const href = withEditorBasePath(router.asPath, x.slug)
                const name = path.basename(x.slug)
                return (
                    <Fragment key={x.slug}>
                        <div className='opacity-50 font-bold'>/</div>
                        <NextLink href={href} className=''>
                            {name}
                        </NextLink>
                    </Fragment>
                )
            })}
        </div>
    )
}
